import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, Info } from "lucide-react";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import ServiceCard from "../components/ServiceCard";
import CTASection from "../components/CTASection";
import { EASE, Reveal } from "../components/Reveal";
import {
  services,
  serviceCategories,
  pricingNote,
  servicesDisclaimer,
} from "../data/services";

const filters = ["All", ...serviceCategories];

export default function ServicesPage() {
  const [params, setParams] = useSearchParams();
  const active = params.get("category") || "All";
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(
    () =>
      active === "All"
        ? services
        : services.filter((s) => s.category === active),
    [active],
  );

  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (e) => e.key === "Escape" && setSelected(null);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [selected]);

  return (
    <>
      <SEO
        title="Salon Services in Banjara Hills | Glow & Blow"
        description="Hair, colour, beauty, grooming and styling services at Glow & Blow Unisex Salon, Banjara Hills, Hyderabad. Contact the salon for current pricing."
      />
      <PageHero
        eyebrow="Services"
        titleLines={[
          <>The Service</>,
          <>
            <em className="italic text-champagne">Directory</em>
          </>,
        ]}
        copy="Hair, colour, treatments, beauty, grooming and styling — explore what we do, then book a visit that works around you."
      />

      <section className="pb-24 md:pb-32" data-testid="services-directory">
        <div className="container-x">
          <Reveal>
            <div
              className="mb-10 flex items-start gap-3 border border-champagne/40 bg-champagne/10 p-4 text-sm text-taupe"
              data-testid="services-disclaimer"
            >
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-champagne-dark" />
              {servicesDisclaimer}
            </div>
          </Reveal>

          <div
            className="mb-12 flex flex-wrap gap-2"
            role="group"
            aria-label="Filter services by category"
          >
            {filters.map((f) => (
              <button
                key={f}
                type="button"
                data-testid={`service-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                aria-pressed={active === f}
                onClick={() =>
                  setParams(f === "All" ? {} : { category: f })
                }
                className={`min-h-[44px] border px-5 text-[12px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${
                  active === f
                    ? "border-ink bg-ink text-white"
                    : "border-line bg-transparent text-soften hover:border-ink hover:text-ink"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {filtered.length ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((s, i) => (
                <ServiceCard key={s.id} service={s} index={i} onView={setSelected} />
              ))}
            </div>
          ) : (
            <div
              className="border border-dashed border-line bg-surface p-16 text-center"
              data-testid="services-empty-state"
            >
              <p className="font-display text-2xl italic text-soften">
                Services in this category will appear here.
              </p>
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {selected && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/85 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setSelected(null)}
            data-testid="service-modal"
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label={selected.name}
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="max-h-[90vh] w-full max-w-xl overflow-y-auto border border-line bg-ivory"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <img
                  src={selected.image}
                  alt={selected.alt}
                  className="aspect-[16/9] w-full object-cover"
                />
                <button
                  type="button"
                  data-testid="service-modal-close"
                  onClick={() => setSelected(null)}
                  aria-label="Close details"
                  className="absolute right-4 top-4 flex h-[44px] w-[44px] items-center justify-center bg-ink/80 text-ivory backdrop-blur-sm transition-colors hover:text-champagne"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-8 md:p-10">
                <p className="eyebrow">{selected.category}</p>
                <h3 className="mt-3 font-display text-3xl font-medium text-ink">
                  {selected.name}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-soften">
                  {selected.description}
                </p>
                <p className="mt-6 border-t border-line pt-5 text-sm italic text-taupe">
                  {selected.price ? selected.price : pricingNote}
                  {selected.duration ? ` · ${selected.duration}` : ""}
                </p>
                <Link
                  to={`/book?service=${encodeURIComponent(selected.name)}`}
                  data-testid="service-modal-book"
                  className="group mt-8 inline-flex min-h-[48px] items-center gap-2.5 bg-ink px-7 text-[13px] font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:bg-taupe"
                >
                  Book Appointment
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <CTASection />
    </>
  );
}
